import { HttpHeaders, HttpInterceptorFn } from '@angular/common/http';
import { InjectionToken, inject, isDevMode } from '@angular/core';
import { finalize } from 'rxjs';
import { LoaderService } from '../services/loader.service';

export const SKIP_LOADER_HEADER = 'x-skip-loader';
export const BLOCKING_LOADER_HEADER = 'x-show-loader';
export const LOADER_MESSAGE_HEADER = 'x-loader-message';
export const LOADER_MONEY_HEADER = 'x-loader-money';

export interface LoaderInterceptorConfig {
  /**
   * Dev only: `console.warn` (once per method + path) for requests that carry
   * neither `skipLoaderHeaders()` nor `blockingLoaderHeaders()`. Off by
   * default; turn it on while auditing a screen to find calls that were never
   * classified (they get no overlay and may have no local feedback either).
   */
  warnUnclassified: boolean;
}

export const LOADER_INTERCEPTOR_CONFIG =
  new InjectionToken<LoaderInterceptorConfig>('LOADER_INTERCEPTOR_CONFIG', {
    providedIn: 'root',
    factory: () => ({ warnUnclassified: false }),
  });

/**
 * Classification marker: "this request has its own local feedback" (inline
 * spinner, table bar, `gd-page-loader`, button `isLoading`...). Since the
 * overlay is opt-in, it no longer changes behavior: no header already means no
 * overlay. It stays so intent is explicit in the code and so the dev-only
 * `warnUnclassified` audit can tell a deliberate call from a forgotten one.
 */
export function skipLoaderHeaders(headers?: HttpHeaders): HttpHeaders {
  return (headers ?? new HttpHeaders()).set(SKIP_LOADER_HEADER, 'true');
}

export interface BlockingLoaderOptions {
  /** Short copy shown under the animation, in Spanish (e.g. "Guardando venta..."). */
  message?: string;
  /**
   * Mark operations that move money or stock. If the 30 s timeout hits, the
   * copy warns the operation may have completed so the user checks first.
   */
  money?: boolean;
  headers?: HttpHeaders;
}

/**
 * Headers for operations that must block the whole UI with the overlay (sales,
 * payments, treasury movements, invoicing). The interceptor strips all of them
 * before sending, so nothing internal reaches the wire.
 */
export function blockingLoaderHeaders(
  options: BlockingLoaderOptions = {},
): HttpHeaders {
  let headers = (options.headers ?? new HttpHeaders()).set(
    BLOCKING_LOADER_HEADER,
    'true',
  );
  if (options.message) {
    headers = headers.set(LOADER_MESSAGE_HEADER, options.message);
  }
  if (options.money) {
    headers = headers.set(LOADER_MONEY_HEADER, 'true');
  }
  return headers;
}

const warnedRequests = new Set<string>();

function warnUnclassified(method: string, urlWithParams: string): void {
  const key = `${method} ${urlWithParams.split('?', 1)[0]}`;
  if (warnedRequests.has(key)) {
    return;
  }
  warnedRequests.add(key);
  console.warn(
    `[loader] Request sin clasificar (usar skipLoaderHeaders() o blockingLoaderHeaders()): ${key}`,
  );
}

/**
 * Opt-in: the overlay is shown only for requests carrying
 * `blockingLoaderHeaders()`. Everything else relies on local feedback.
 */
export const loaderInterceptor: HttpInterceptorFn = (request, next) => {
  const loaderService = inject(LoaderService);
  const config = inject(LOADER_INTERCEPTOR_CONFIG);

  const isSkip = request.headers.get(SKIP_LOADER_HEADER) === 'true';
  const isBlocking = request.headers.get(BLOCKING_LOADER_HEADER) === 'true';
  const message = request.headers.get(LOADER_MESSAGE_HEADER) ?? undefined;
  const isMoney = request.headers.get(LOADER_MONEY_HEADER) === 'true';

  const cleanRequest = request.clone({
    headers: request.headers
      .delete(SKIP_LOADER_HEADER)
      .delete(BLOCKING_LOADER_HEADER)
      .delete(LOADER_MESSAGE_HEADER)
      .delete(LOADER_MONEY_HEADER),
  });

  if (isDevMode()) {
    if (isSkip && isBlocking) {
      console.warn(
        `[loader] ${request.method} ${request.url} lleva skip y blocking a la vez: gana blocking.`,
      );
    }
    if (!isSkip && !isBlocking && config.warnUnclassified) {
      warnUnclassified(request.method, request.urlWithParams);
    }
  }

  if (!isBlocking) {
    return next(cleanRequest);
  }

  const token = loaderService.show({ message, money: isMoney });

  return next(cleanRequest).pipe(finalize(() => loaderService.hide(token)));
};
